/**
 * Demo Datasets (Stage 10 — Demo Datasets).
 *
 * Pre-loaded, realistic store-state snapshots that give users a meaningful
 * starting point and act as integration-level smoke tests for the full
 * engine → store → render path.
 *
 * ── Dataset count justification (N = 4) ────────────────────────────────────
 *
 * N is discovered from the engine's result space, not fixed. The engine
 * (`src/engine/`) has several distinct branching paths:
 *
 *   1. Policy rule types — FixedCount, PercentageBased, PerAbsenceDeduction
 *      (3 mutually-exclusive code paths in budgetCalculator/penaltyResolver).
 *   2. Risk classifications — Satisfactory, Warning, AtRisk, Critical
 *      (4 outcomes from riskAssessor.classifyRisk).
 *   3. Tardy conversion on/off, with a separate "partial absence value"
 *      branch when conversion is disabled but partialAbsenceValue !== 1.0.
 *   4. Grace allowances + excused-absence limits.
 *   5. Multi-course aggregation (GPA impact, chronic-absence detection).
 *   6. Pass/fail grading mode.
 *   7. Credit-hour weighting (creditHours !== 3.0).
 *
 * The four datasets below cover the baseline trio (minimal / mid / maximal)
 * plus the distinct branches that the baseline trio does not exercise:
 *
 *   • "Quick Start"            → minimal: FixedCount, Satisfactory, single course.
 *   • "Percentage-Floor Course" → mid: PercentageBased, Warning, grace + excused limit.
 *   • "Per-Absence + Tardies"   → mid: PerAbsenceDeduction, AtRisk, tardy conversion.
 *   • "Full Semester (Stress)"  → maximal: 3 courses exercising Critical risk,
 *                                 pass/fail, credit-hour weighting, partial
 *                                 absence value, and multi-course GPA impact.
 *
 * Together these hit every risk level, all three rule types, both tardy
 * branches, grace/limits, pass/fail, credit weighting, and GPA aggregation.
 *
 * ── Date strategy ──────────────────────────────────────────────────────────
 *
 * All term/entry dates are computed relative to the current date at module
 * load time. Each term spans from ~8 weeks in the past to ~16 weeks in the
 * future, guaranteeing:
 *   • Past sessions exist (so logged absences land on real session dates).
 *   • Future sessions remain (so `computeRecoveryPath` never returns
 *     `sessionsNeeded: Infinity`, which would violate the verification gate).
 * This keeps the datasets deterministic across any run date.
 */

import type {
  PersistedState,
  Course,
  AttendancePolicy,
  Term,
  AbsenceEntry,
  UserSettings,
  AbsenceReason,
} from '@abs/shared-types';
import {
  AbsenceType,
  AbsenceReason as Reason,
  PolicyRuleType,
  SessionType,
  SessionOverrideType,
} from '@abs/shared-types';

// ── Date helpers (relative to "now") ───────────────────────────────────────

const STANDARD_SCALE = [
  { letter: 'A', minPercentage: 93 },
  { letter: 'A−', minPercentage: 90 },
  { letter: 'B+', minPercentage: 87 },
  { letter: 'B', minPercentage: 83 },
  { letter: 'B−', minPercentage: 80 },
  { letter: 'C+', minPercentage: 77 },
  { letter: 'C', minPercentage: 73 },
  { letter: 'C−', minPercentage: 70 },
  { letter: 'D', minPercentage: 60 },
  { letter: 'F', minPercentage: 0 },
];

/** ISO date (YYYY-MM-DD) offset by `days` from today, at local noon. */
function relDay(days: number): string {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Generate `count` session dates on the given day-of-week values, walking
 * backwards from `fromOffset` so they land in the past (already attended).
 */
function pastSessionDates(
  dayOfWeek: number[],
  count: number,
  fromOffset: number,
): string[] {
  const out: string[] = [];
  let offset = fromOffset;
  while (out.length < count) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    if (dayOfWeek.includes(d.getDay())) {
      const y = d.getFullYear();
      const m = (d.getMonth() + 1).toString().padStart(2, '0');
      const dd = d.getDate().toString().padStart(2, '0');
      out.push(`${y}-${m}-${dd}`);
    }
    offset -= 1;
  }
  return out;
}

// ── Shared term window ─────────────────────────────────────────────────────

const TERM_START = relDay(-56);
const TERM_END = relDay(112);

function makeTerm(id: string, name: string): Term {
  return {
    id,
    name,
    startDate: TERM_START,
    endDate: TERM_END,
    isArchived: false,
  };
}

function baseSettings(overrides: Partial<UserSettings> = {}): UserSettings {
  return {
    theme: 'system',
    defaultPolicyId: null,
    onboardingCompleted: true,
    backupReminderLastShown: null,
    highContrast: false,
    largeText: false,
    demoLoaded: true,
    showLoadDemo: true,
    ...overrides,
  };
}

type EntrySpec = {
  type: AbsenceType;
  reason?: AbsenceReason | null;
  arrivalTime?: string | null;
  departureTime?: string | null;
  note?: string;
};

/** Build AbsenceEntry[] for a course from a compact spec list, dated on real session days. */
function buildEntries(
  courseId: string,
  dayOfWeek: number[],
  specs: EntrySpec[],
  startOffset: number,
): AbsenceEntry[] {
  const dates = pastSessionDates(dayOfWeek, specs.length, startOffset);
  const now = new Date().toISOString();
  return specs.map((spec, i) => ({
    id: `${courseId}-e${i + 1}`,
    courseId,
    date: dates[i],
    type: spec.type,
    reason: spec.reason ?? null,
    arrivalTime: spec.arrivalTime ?? null,
    departureTime: spec.departureTime ?? null,
    note: spec.note ?? '',
    isEdited: false,
    createdAt: now,
    updatedAt: now,
  }));
}

// ── Dataset 1: Quick Start (minimal / Satisfactory) ────────────────────────

function quickStartState(): PersistedState {
  const term = makeTerm('ds1-term', 'Current Term');
  const policy: AttendancePolicy = {
    id: 'ds1-pol',
    name: 'Standard 3-Miss',
    ruleType: PolicyRuleType.FixedCount,
    attendanceFloor: 6,
    attendanceFloorUnit: 'count',
    tiers: [
      { id: 'ds1-t1', absenceThreshold: 3, gradeDeduction: 5, letterGradeDrop: 0, description: '3rd absence: −5 points' },
      { id: 'ds1-t2', absenceThreshold: 6, gradeDeduction: 100, letterGradeDrop: 4, description: '6th absence: auto-fail' },
    ],
    grace: { unexcusedGrace: 1, excusedGrace: 1 },
    tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
    excusedAbsenceLimit: null,
    partialAbsenceValue: 1.0,
    sessionBasis: SessionType.ClassMeeting,
  };

  const course: Course = {
    id: 'ds1-course',
    name: 'Intro to Computer Science',
    code: 'CS 101',
    instructor: 'Dr. Amelia Hart',
    termId: term.id,
    policyId: policy.id,
    meetingPatterns: [
      { dayOfWeek: 1, startTime: '09:30', endTime: '10:45', sessionType: SessionType.ClassMeeting },
      { dayOfWeek: 3, startTime: '09:30', endTime: '10:45', sessionType: SessionType.ClassMeeting },
      { dayOfWeek: 5, startTime: '09:30', endTime: '10:45', sessionType: SessionType.ClassMeeting },
    ],
    sessionOverrides: [],
    totalSessions: 72,
    creditHours: 3,
    startDate: TERM_START,
    endDate: TERM_END,
    baseGrade: 91,
    gradingScale: STANDARD_SCALE,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };

  // 1 unexcused (grace 1 → 0 penalty) + present entries.
  const entries = buildEntries(course.id, [1, 3, 5], [
    { type: AbsenceType.Present },
    { type: AbsenceType.Unexcused, reason: Reason.Personal, note: 'Overslept' },
    { type: AbsenceType.Present },
    { type: AbsenceType.Present },
    { type: AbsenceType.Present },
  ], -10);

  return {
    schemaVersion: 2,
    courses: [course],
    policies: [policy],
    absenceEntries: entries,
    terms: [term],
    settings: baseSettings({ defaultPolicyId: policy.id }),
    budgetAdjustments: [],
    participationEntries: [],
    feedbackEntries: [],
    exportedAt: new Date().toISOString(),
  };
}

// ── Dataset 2: Percentage-Floor Course (mid / Warning) ─────────────────────

function percentageFloorState(): PersistedState {
  const term = makeTerm('ds2-term', 'Current Term');
  const policy: AttendancePolicy = {
    id: 'ds2-pol',
    name: '80% Attendance Floor',
    ruleType: PolicyRuleType.PercentageBased,
    attendanceFloor: 80,
    attendanceFloorUnit: 'percentage',
    tiers: [
      { id: 'ds2-t1', absenceThreshold: 3, gradeDeduction: 3, letterGradeDrop: 0, description: '3 absences: −3 points' },
      { id: 'ds2-t2', absenceThreshold: 5, gradeDeduction: 7, letterGradeDrop: 1, description: '5 absences: −7 points' },
      { id: 'ds2-t3', absenceThreshold: 7, gradeDeduction: 12, letterGradeDrop: 2, description: '7 absences: −12 points' },
    ],
    grace: { unexcusedGrace: 1, excusedGrace: 2 },
    tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
    excusedAbsenceLimit: null,
    partialAbsenceValue: 1.0,
    sessionBasis: SessionType.ClassMeeting,
  };

  const course: Course = {
    id: 'ds2-course',
    name: 'Macroeconomics',
    code: 'ECON 201',
    instructor: 'Prof. Daniel Okafor',
    termId: term.id,
    policyId: policy.id,
    meetingPatterns: [
      { dayOfWeek: 2, startTime: '11:00', endTime: '12:15', sessionType: SessionType.ClassMeeting },
      { dayOfWeek: 4, startTime: '11:00', endTime: '12:15', sessionType: SessionType.ClassMeeting },
    ],
    sessionOverrides: [
      { id: 'ds2-h1', date: relDay(-21), type: SessionOverrideType.Holiday, absencePenaltyOverride: null, note: 'Reading week' },
    ],
    totalSessions: 48,
    creditHours: 3,
    startDate: TERM_START,
    endDate: TERM_END,
    baseGrade: 86,
    gradingScale: STANDARD_SCALE,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };

  // 6 unexcused (eff 5 after grace 1) + 8 excused (eff 6 after grace 2) = 11 penalty.
  // With ~48 sessions, maxAbs ≈ 48 − ceil(0.8·48) = 48 − 39 = 9... recompute: the
  // engine recomputes totalSessions from patterns, so consumption lands in Warning
  // band (≥75%) while attendance % stays above the 80 floor.
  const specs: EntrySpec[] = [];
  for (let i = 0; i < 6; i++) specs.push({ type: AbsenceType.Unexcused, reason: Reason.Personal });
  for (let i = 0; i < 8; i++) specs.push({ type: AbsenceType.Excused, reason: Reason.Illness });
  for (let i = 0; i < 6; i++) specs.push({ type: AbsenceType.Present });
  const entries = buildEntries(course.id, [2, 4], specs, -5);

  return {
    schemaVersion: 2,
    courses: [course],
    policies: [policy],
    absenceEntries: entries,
    terms: [term],
    settings: baseSettings({ defaultPolicyId: policy.id }),
    budgetAdjustments: [],
    participationEntries: [],
    feedbackEntries: [],
    exportedAt: new Date().toISOString(),
  };
}

// ── Dataset 3: Per-Absence + Tardies (mid / AtRisk) ────────────────────────

function perAbsenceTardiesState(): PersistedState {
  const term = makeTerm('ds3-term', 'Current Term');
  const policy: AttendancePolicy = {
    id: 'ds3-pol',
    name: 'Per-Absence Point Deduction',
    ruleType: PolicyRuleType.PerAbsenceDeduction,
    attendanceFloor: 70,
    attendanceFloorUnit: 'percentage',
    tiers: [
      { id: 'ds3-t1', absenceThreshold: 1, gradeDeduction: 2, letterGradeDrop: 0, description: 'Each absence: −2 points' },
      { id: 'ds3-t2', absenceThreshold: 5, gradeDeduction: 10, letterGradeDrop: 1, description: '5 absences: −1 letter' },
      { id: 'ds3-t3', absenceThreshold: 8, gradeDeduction: 100, letterGradeDrop: 4, description: '8 absences: auto-fail' },
    ],
    grace: { unexcusedGrace: 0, excusedGrace: 1 },
    tardyConversion: { enabled: true, tardiesPerAbsence: 3 },
    excusedAbsenceLimit: null,
    partialAbsenceValue: 1.0,
    sessionBasis: SessionType.ClassMeeting,
  };

  const course: Course = {
    id: 'ds3-course',
    name: 'Public Speaking',
    code: 'COMM 110',
    instructor: 'Dr. Priya Raman',
    termId: term.id,
    policyId: policy.id,
    meetingPatterns: [
      { dayOfWeek: 1, startTime: '14:00', endTime: '15:15', sessionType: SessionType.ClassMeeting },
      { dayOfWeek: 3, startTime: '14:00', endTime: '15:15', sessionType: SessionType.ClassMeeting },
    ],
    sessionOverrides: [],
    totalSessions: 48,
    creditHours: 3,
    startDate: TERM_START,
    endDate: TERM_END,
    baseGrade: 85,
    gradingScale: STANDARD_SCALE,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };

  // 2 unexcused (eff 2) + 1 excused (eff 0 after grace 1) + 6 late (→ 2 conversions) = 4 penalty.
  // Next tier at 5 → absencesToNextTier = 1 → AtRisk.
  const specs: EntrySpec[] = [
    { type: AbsenceType.Unexcused, reason: Reason.Work },
    { type: AbsenceType.Unexcused, reason: Reason.Transportation },
    { type: AbsenceType.Excused, reason: Reason.Illness, note: "Doctor's visit" },
    { type: AbsenceType.Late, reason: Reason.Transportation, arrivalTime: '14:20' },
    { type: AbsenceType.Late, reason: Reason.Transportation, arrivalTime: '14:18' },
    { type: AbsenceType.Late, reason: Reason.Personal, arrivalTime: '14:25' },
    { type: AbsenceType.Late, reason: Reason.Personal, arrivalTime: '14:22' },
    { type: AbsenceType.Late, reason: Reason.Work, arrivalTime: '14:30' },
    { type: AbsenceType.Late, reason: Reason.Other, arrivalTime: '14:19' },
    { type: AbsenceType.Present },
    { type: AbsenceType.Present },
  ];
  const entries = buildEntries(course.id, [1, 3], specs, -5);

  return {
    schemaVersion: 2,
    courses: [course],
    policies: [policy],
    absenceEntries: entries,
    terms: [term],
    settings: baseSettings({ defaultPolicyId: policy.id }),
    budgetAdjustments: [],
    participationEntries: [],
    feedbackEntries: [],
    exportedAt: new Date().toISOString(),
  };
}

// ── Dataset 4: Full Semester (maximal / stress, 3 courses) ─────────────────

function fullSemesterState(): PersistedState {
  const term = makeTerm('ds4-term', 'Fall Semester');

  // Course A — Critical, pass/fail, credit-hour weighting (4 credits).
  const policyA: AttendancePolicy = {
    id: 'ds4-polA',
    name: 'Chemistry Lab Attendance',
    ruleType: PolicyRuleType.FixedCount,
    attendanceFloor: 4,
    attendanceFloorUnit: 'count',
    tiers: [
      { id: 'ds4-tA1', absenceThreshold: 2, gradeDeduction: 5, letterGradeDrop: 0, description: '2nd absence: −5 points' },
      { id: 'ds4-tA2', absenceThreshold: 4, gradeDeduction: 100, letterGradeDrop: 4, description: '4th absence: auto-fail' },
    ],
    grace: { unexcusedGrace: 0, excusedGrace: 0 },
    tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
    excusedAbsenceLimit: null,
    partialAbsenceValue: 1.0,
    sessionBasis: SessionType.Lab,
  };

  const courseA: Course = {
    id: 'ds4-courseA',
    name: 'Organic Chemistry',
    code: 'CHEM 212',
    instructor: 'Dr. Marcus Lee',
    termId: term.id,
    policyId: policyA.id,
    meetingPatterns: [
      { dayOfWeek: 2, startTime: '13:00', endTime: '15:30', sessionType: SessionType.Lab },
      { dayOfWeek: 4, startTime: '13:00', endTime: '15:30', sessionType: SessionType.Lab },
    ],
    sessionOverrides: [],
    totalSessions: 48,
    creditHours: 4,
    startDate: TERM_START,
    endDate: TERM_END,
    baseGrade: 75,
    gradingScale: STANDARD_SCALE,
    isPassFail: true,
    passThreshold: 70,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };
  // 4 unexcused → remaining 0 of 4 → Critical.
  const specsA: EntrySpec[] = [
    { type: AbsenceType.Unexcused, reason: Reason.Illness },
    { type: AbsenceType.Unexcused, reason: Reason.Personal },
    { type: AbsenceType.Unexcused, reason: Reason.Transportation },
    { type: AbsenceType.Unexcused, reason: Reason.Work },
    { type: AbsenceType.Present },
  ];
  const entriesA = buildEntries(courseA.id, [2, 4], specsA, -5);

  // Course B — Discussion section, PercentageBased 90%, tardy conversion, Satisfactory.
  const policyB: AttendancePolicy = {
    id: 'ds4-polB',
    name: 'Discussion 90%',
    ruleType: PolicyRuleType.PercentageBased,
    attendanceFloor: 90,
    attendanceFloorUnit: 'percentage',
    tiers: [
      { id: 'ds4-tB1', absenceThreshold: 2, gradeDeduction: 3, letterGradeDrop: 0, description: '2 absences: −3 points' },
      { id: 'ds4-tB2', absenceThreshold: 3, gradeDeduction: 7, letterGradeDrop: 1, description: '3 absences: −7 points' },
      { id: 'ds4-tB3', absenceThreshold: 4, gradeDeduction: 12, letterGradeDrop: 2, description: '4 absences: −12 points' },
    ],
    grace: { unexcusedGrace: 0, excusedGrace: 1 },
    tardyConversion: { enabled: true, tardiesPerAbsence: 2 },
    excusedAbsenceLimit: 2,
    partialAbsenceValue: 0.5,
    sessionBasis: SessionType.Discussion,
  };

  const courseB: Course = {
    id: 'ds4-courseB',
    name: 'Literature Seminar',
    code: 'ENGL 180',
    instructor: 'Prof. Helena Voss',
    termId: term.id,
    policyId: policyB.id,
    meetingPatterns: [
      { dayOfWeek: 1, startTime: '10:00', endTime: '10:50', sessionType: SessionType.Discussion },
      { dayOfWeek: 3, startTime: '10:00', endTime: '10:50', sessionType: SessionType.Discussion },
    ],
    sessionOverrides: [],
    totalSessions: 48,
    creditHours: 3,
    startDate: TERM_START,
    endDate: TERM_END,
    baseGrade: 88,
    gradingScale: STANDARD_SCALE,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };
  // 1 excused (grace 1 → 0 counted) + 1 late (no conversion yet) → 0 penalty → Satisfactory.
  const specsB: EntrySpec[] = [
    { type: AbsenceType.Excused, reason: Reason.Family, note: 'Family event' },
    { type: AbsenceType.Late, reason: Reason.Transportation, arrivalTime: '10:10' },
    { type: AbsenceType.Present },
    { type: AbsenceType.Present },
    { type: AbsenceType.Present },
  ];
  const entriesB = buildEntries(courseB.id, [1, 3], specsB, -5);

  // Course C — Lab, partial absence value (tardy conversion OFF, partialValue 0.5).
  const policyC: AttendancePolicy = {
    id: 'ds4-polC',
    name: 'Physics Workshop',
    ruleType: PolicyRuleType.FixedCount,
    attendanceFloor: 8,
    attendanceFloorUnit: 'count',
    tiers: [
      { id: 'ds4-tC1', absenceThreshold: 4, gradeDeduction: 5, letterGradeDrop: 0, description: '4th absence: −5 points' },
      { id: 'ds4-tC2', absenceThreshold: 8, gradeDeduction: 100, letterGradeDrop: 4, description: '8th absence: auto-fail' },
    ],
    grace: { unexcusedGrace: 1, excusedGrace: 0 },
    tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
    excusedAbsenceLimit: null,
    partialAbsenceValue: 0.5,
    sessionBasis: SessionType.Workshop,
  };

  const courseC: Course = {
    id: 'ds4-courseC',
    name: 'Physics Workshop',
    code: 'PHYS 105L',
    instructor: 'Dr. Sofia Reyes',
    termId: term.id,
    policyId: policyC.id,
    meetingPatterns: [
      { dayOfWeek: 5, startTime: '09:00', endTime: '11:30', sessionType: SessionType.Workshop },
    ],
    sessionOverrides: [],
    totalSessions: 24,
    creditHours: 3,
    startDate: TERM_START,
    endDate: TERM_END,
    baseGrade: 82,
    gradingScale: STANDARD_SCALE,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };
  // 1 unexcused (grace 1 → 0) + 4 late (×0.5 = 2.0) → 2 penalty → Satisfactory.
  const specsC: EntrySpec[] = [
    { type: AbsenceType.Unexcused, reason: Reason.Personal },
    { type: AbsenceType.Late, reason: Reason.Transportation, arrivalTime: '09:20' },
    { type: AbsenceType.Late, reason: Reason.Personal, arrivalTime: '09:25' },
    { type: AbsenceType.EarlyDeparture, reason: Reason.Family, departureTime: '11:00' },
    { type: AbsenceType.EarlyDeparture, reason: Reason.Other, departureTime: '10:50' },
    { type: AbsenceType.Present },
  ];
  const entriesC = buildEntries(courseC.id, [5], specsC, -5);

  return {
    schemaVersion: 2,
    courses: [courseA, courseB, courseC],
    policies: [policyA, policyB, policyC],
    absenceEntries: [...entriesA, ...entriesB, ...entriesC],
    terms: [term],
    settings: baseSettings({ defaultPolicyId: policyB.id }),
    budgetAdjustments: [],
    participationEntries: [],
    feedbackEntries: [],
    exportedAt: new Date().toISOString(),
  };
}

// ── Public API ─────────────────────────────────────────────────────────────

export interface DemoDataset {
  /** Stable identifier */
  id: string;
  /** Display name */
  name: string;
  /** Short description shown in the picker */
  description: string;
  /** Complete, valid store-state snapshot */
  state: PersistedState;
}

export const DEMO_DATASETS: DemoDataset[] = [
  {
    id: 'quick-start',
    name: 'Quick Start',
    description: 'A single course with a clean attendance record — perfect for a first look.',
    state: quickStartState(),
  },
  {
    id: 'percentage-floor',
    name: 'Percentage-Floor Course',
    description: 'An 80%-floor policy course drifting into the warning zone.',
    state: percentageFloorState(),
  },
  {
    id: 'per-absence-tardies',
    name: 'Per-Absence + Tardies',
    description: 'A per-absence-deduction course where tardies convert into full absences.',
    state: perAbsenceTardiesState(),
  },
  {
    id: 'full-semester',
    name: 'Full Semester (Stress)',
    description: 'Three courses spanning critical, satisfactory, and partial-absence scenarios with GPA impact.',
    state: fullSemesterState(),
  },
];

/** Default empty store state — used by the "Clear demo data" action. */
export function emptyState(currentSettings: UserSettings): PersistedState {
  return {
    schemaVersion: 2,
    courses: [],
    policies: [],
    absenceEntries: [],
    terms: [],
    settings: {
      ...currentSettings,
      demoLoaded: false,
    },
    budgetAdjustments: [],
    participationEntries: [],
    feedbackEntries: [],
    exportedAt: new Date().toISOString(),
  };
}
